import { ArrowLeft, MessageSquare, Check, X, Package, CheckCircle2, Loader2, ShoppingCart, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { BottomNav } from "./bottom-nav";
import { Button } from "./button";
import { CounterOfferModal } from "./counter-offer-modal";
import { AdImage } from "@/app/components/common/ad-image";
import { CollapsibleNestedOffer } from "@/app/components/collapsible-nested-offer";
import { ParentOfferBlock } from "@/app/components/parent-offer-block";
import { getAdImage } from "@/app/utils/ad-utils";
import { getThreadViewItems } from "@/app/utils/offer-utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/components/ui/avatar";
import * as authClient from "@/app/api/auth-client";
import { useAccountNavEnabled } from "@/app/context/account-nav-context";
import { toast } from "sonner";
import type { Ad } from "./ad-card";
import { toastNextTask } from "@/app/utils/toast-next-task";

interface ReceivedOffer {
  id: string;
  buyerName: string;
  buyerAvatar: string;
  buyerProfileImageUrl?: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  message?: string;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "withdrawn" | "countered";
  createdAt: Date;
  /** True when current user is the buyer (e.g. accepted counter); show "Add to cart". */
  isCurrentUserBuyer?: boolean;
  /** Set when this offer is a counter (links to original offer for nesting). */
  parentOfferId?: string;
}

interface ReceivedOffersPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onAdClick?: (adId: string) => void;
  receivedOffers?: ReceivedOffer[];
  /** When provided, load received offers from API on mount. May return the list for callers that need it (e.g. after 400). */
  loadReceivedOffers?: () => Promise<ReceivedOffer[] | void | unknown>;
  /** Call after accept/decline/counter success to refresh notification bell count. */
  onNotificationCountRefetch?: () => void;
  /** When provided, show "Add to cart" for accepted offers where current user is the buyer. */
  onAddOfferToCart?: (offerId: string) => void;
  /** Offer IDs already in the cart (show "Remove" instead of "Add to cart"). */
  offerIdsInCart?: string[];
  onRemoveOfferFromCart?: (offerId: string) => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export function ReceivedOffersPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onAdClick,
  receivedOffers,
  loadReceivedOffers,
  onNotificationCountRefetch,
  onAddOfferToCart,
  offerIdsInCart = [],
  onRemoveOfferFromCart,
}: ReceivedOffersPageProps) {
  const [loading, setLoading] = useState(!!loadReceivedOffers);
  const [offers, setOffers] = useState<ReceivedOffer[]>(receivedOffers ?? []);
  const [filter, setFilter] = useState<"all" | "pending" | "accepted" | "rejected" | "withdrawn" | "countered">("all");
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<ReceivedOffer | null>(null);
  const [actionOfferId, setActionOfferId] = useState<string | null>(null);
  const accountNav = useAccountNavEnabled();

  useEffect(() => {
    setOffers(receivedOffers ?? []);
  }, [receivedOffers]);

  useEffect(() => {
    if (loadReceivedOffers) {
      setLoading(true);
      loadReceivedOffers().finally(() => setLoading(false));
    }
  }, [loadReceivedOffers]);

  useEffect(() => {
    if (!accountNav) {
      setCounterOfferModalOpen(false);
      setSelectedOffer(null);
    }
  }, [accountNav]);

  const handleAcceptOffer = async (offerId: string) => {
    if (!accountNav || !loadReceivedOffers) return;
    setActionOfferId(offerId);
    try {
      await authClient.acceptOffer(offerId);
      await loadReceivedOffers();
      onNotificationCountRefetch?.();
      toastNextTask(() => toast.success("Oferta aceita."));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toastNextTask(() => toast.error("Você não tem permissão para aceitar esta oferta."));
      } else if (status === 400) {
        const next = await loadReceivedOffers?.();
        if (Array.isArray(next)) {
          setOffers(next);
          const stillPending = next.find((o) => o.id === offerId && o.status === "pending");
          if (stillPending) setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, status: "rejected" as const } : o)));
        }
        toastNextTask(() => toast.error("Esta oferta não está mais pendente."));
      } else {
        toastNextTask(() =>
          toast.error((err as Error)?.message ?? "Não foi possível aceitar a oferta.")
        );
      }
    } finally {
      setActionOfferId(null);
    }
  };

  const handleRejectOffer = async (offerId: string) => {
    if (!accountNav || !loadReceivedOffers) return;
    setActionOfferId(offerId);
    try {
      await authClient.declineOffer(offerId);
      await loadReceivedOffers();
      onNotificationCountRefetch?.();
      toastNextTask(() => toast.success("Oferta recusada."));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toastNextTask(() => toast.error("Você não tem permissão para recusar esta oferta."));
      } else if (status === 400) {
        const next = await loadReceivedOffers?.();
        if (Array.isArray(next)) {
          setOffers(next);
          const stillPending = next.find((o) => o.id === offerId && o.status === "pending");
          if (stillPending) setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, status: "rejected" as const } : o)));
        }
        toastNextTask(() => toast.error("Esta oferta não está mais pendente."));
      } else {
        toastNextTask(() =>
          toast.error((err as Error)?.message ?? "Não foi possível recusar a oferta.")
        );
      }
    } finally {
      setActionOfferId(null);
    }
  };

  const handleOpenCounterOffer = (offer: ReceivedOffer) => {
    if (!accountNav) return;
    setSelectedOffer(offer);
    setCounterOfferModalOpen(true);
  };

  const handleSubmitCounterOffer = async (counterOffer: {
    cashAmount: number;
    message: string;
    selectedTradeInAdIds?: string[];
  }) => {
    if (!accountNav || !selectedOffer) return;
    setActionOfferId(selectedOffer.id);
    try {
      await authClient.counterOffer(selectedOffer.id, {
        amount_cents: Math.round(counterOffer.cashAmount * 100),
        product_ad_ids: counterOffer.selectedTradeInAdIds ?? [],
        message: counterOffer.message.trim() || undefined,
        type: selectedOffer.offerType === "product-plus-cash" ? "product_money" : "money",
      });
      setCounterOfferModalOpen(false);
      setSelectedOffer(null);
      await loadReceivedOffers?.();
      onNotificationCountRefetch?.();
      toastNextTask(() => toast.success("Contra-oferta enviada."));
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 403) {
        toastNextTask(() => toast.error("Você não tem permissão para fazer contra-oferta."));
      } else if (status === 400) {
        const offerId = selectedOffer.id;
        const next = await loadReceivedOffers?.();
        if (Array.isArray(next)) {
          setOffers(next);
          const stillPending = next.find((o) => o.id === offerId && o.status === "pending");
          if (stillPending) setOffers((prev) => prev.map((o) => (o.id === offerId ? { ...o, status: "rejected" as const } : o)));
        }
        toastNextTask(() => toast.error("Esta oferta não está mais pendente."));
      } else {
        toastNextTask(() =>
          toast.error((err as Error)?.message ?? "Não foi possível enviar a contra-oferta.")
        );
      }
    } finally {
      setActionOfferId(null);
    }
  };

  const filteredOffers = offers.filter((offer) => {
    if (filter === "all") return true;
    return offer.status === filter;
  });

  const getStatusBadge = (status: ReceivedOffer["status"]) => {
    const pill = "px-2 py-1 rounded-full text-xs whitespace-nowrap shrink-0";
    switch (status) {
      case "pending":
        return <span className={`${pill} bg-yellow-500/20 text-yellow-700 dark:text-yellow-300`}>Pendente</span>;
      case "accepted":
        return <span className={`${pill} bg-green-500/20 text-green-700 dark:text-green-300`}>Aceita</span>;
      case "rejected":
        return <span className={`${pill} bg-red-500/20 text-red-700 dark:text-red-300`}>Recusada</span>;
      case "withdrawn":
        return <span className={`${pill} bg-gray-500/20 text-gray-700 dark:text-gray-300`}>Retirada pelo comprador</span>;
      case "countered":
        return <span className={`${pill} bg-blue-500/20 text-blue-700 dark:text-blue-300`}>Contra-oferta feita</span>;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Ofertas Recebidas</h1>
            <p className="text-sm text-muted-foreground">
              {filteredOffers.length} {filteredOffers.length === 1 ? "oferta" : "ofertas"}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "all" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "pending" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setFilter("accepted")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "accepted" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Aceitas
          </button>
          <button
            onClick={() => setFilter("rejected")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "rejected" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Recusadas
          </button>
          <button
            onClick={() => setFilter("withdrawn")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "withdrawn" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Retiradas
          </button>
          <button
            onClick={() => setFilter("countered")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === "countered" ? "bg-primary text-white" : "bg-accent text-foreground"
            }`}
          >
            Contra-oferta
          </button>
        </div>
      </div>

      {/* Offers List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-3" />
            <p className="text-muted-foreground">Carregando ofertas...</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma oferta encontrada</p>
          </div>
        ) : (
          getThreadViewItems(filteredOffers).map((item) => {
            const offer = item.type === "single" ? item.offer : item.currentOffer;
            const card = (
              <div
                className={`rounded-xl p-4 space-y-4 border-2 transition-all duration-300 ${
                  offer.status === "accepted"
                    ? "bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-orange-500/10 border-orange-300/50 shadow-lg shadow-orange-500/20"
                    : "bg-card border-border"
                }`}
              >
              {/* Accepted Offer Special Header: buyer sees Add to cart, seller sees Contact the buyer */}
              {offer.status === "accepted" && (
                <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 text-white px-4 py-2 rounded-lg -mt-4 -mx-4 mb-4 flex items-center justify-center gap-2 shadow-md">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <span className="font-medium text-center">
                    {offer.isCurrentUserBuyer
                      ? accountNav
                        ? offerIdsInCart.includes(offer.id)
                          ? "Oferta Aceita! Esta oferta já está no seu carrinho"
                          : "Oferta Aceita! Agora você pode adicionar ao carrinho"
                        : "Oferta aceita! Complete seu cadastro para adicionar ao carrinho."
                      : accountNav
                        ? "Oferta Aceita! Você pode entrar em contato com o comprador"
                        : "Oferta aceita! Complete seu cadastro para conversar com o comprador no chat."}
                  </span>
                </div>
              )}

              {/* Buyer Info & Status */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Avatar className="w-10 h-10 shrink-0">
                    {offer.buyerProfileImageUrl ? (
                      <AvatarImage src={offer.buyerProfileImageUrl} alt={offer.buyerName} />
                    ) : null}
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                      {offer.buyerAvatar}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{offer.buyerName}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(offer.createdAt)}</p>
                  </div>
                </div>
                <div className="shrink-0 pt-0.5">{getStatusBadge(offer.status)}</div>
              </div>

              {/* Target Ad - Now Clickable */}
              <button
                onClick={() => onAdClick?.(offer.targetAd.id)}
                className="bg-accent/30 rounded-lg p-3 w-full text-left hover:bg-accent/50 transition-colors"
              >
                <p className="text-xs text-muted-foreground mb-2">Produto anunciado:</p>
                <div className="flex gap-3">
                  <AdImage
                    src={getAdImage(offer.targetAd)}
                    alt={offer.targetAd.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-medium text-sm">{offer.targetAd.title}</h3>
                    <p className="text-lg font-semibold text-primary">{formatCurrency(offer.targetAd.price)}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Clique para ver detalhes →</p>
                  </div>
                </div>
              </button>

              {offer.parentOfferId && (
                <p className="text-xs text-muted-foreground pl-2 border-l-2 border-primary/50">Contra-oferta (atual)</p>
              )}

              {/* Offer Details */}
              <div className="space-y-3">
                <p className="text-sm font-medium">{offer.parentOfferId ? "Detalhes da Contra-Oferta" : "Detalhes da Oferta"}:</p>

                {offer.tradeInAds && offer.tradeInAds.length > 0 && (
                  <div className="bg-background rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">
                      {offer.tradeInAds.length === 1 ? "Produto oferecido:" : "Produtos oferecidos:"}
                    </p>
                    <div className="space-y-2">
                      {offer.tradeInAds.map((tradeAd) => (
                        <div key={tradeAd.id} className="flex gap-2">
                          <AdImage
                            src={tradeAd.image}
                            alt={tradeAd.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div>
                            <p className="text-sm font-medium">{tradeAd.title}</p>
                            <p className="text-sm text-primary">{formatCurrency(tradeAd.value)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-background rounded-lg p-3 space-y-2">
                  {offer.tradeInAds && offer.tradeInAds.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Valor dos produtos:</span>
                      <span className="font-medium">
                        {formatCurrency(offer.tradeInAds.reduce((sum, ad) => sum + ad.value, 0))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Pagamento em dinheiro:</span>
                    <span className="font-medium">{formatCurrency(offer.cashAmount)}</span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-medium">Valor total da oferta:</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(offer.totalOfferValue)}
                      </span>
                    </div>
                  </div>
                </div>

                {offer.message && (
                  <div className="bg-accent/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Mensagem:</p>
                    <p className="text-sm">{offer.message}</p>
                  </div>
                )}
              </div>

              {/* Actions - Only for pending initial offers; counter (pending with parentOfferId) = seller waiting for buyer, so no CTAs */}
              {offer.status === "pending" && !offer.parentOfferId && (
                accountNav ? (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptOffer(offer.id)}
                        disabled={actionOfferId !== null}
                        className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                      >
                        {actionOfferId === offer.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Aceitar
                      </button>
                      <button
                        onClick={() => handleRejectOffer(offer.id)}
                        disabled={actionOfferId !== null}
                        className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                      >
                        {actionOfferId === offer.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                        Recusar
                      </button>
                    </div>
                    <button
                      onClick={() => handleOpenCounterOffer(offer)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Fazer Contra-Oferta
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground rounded-lg border border-border bg-muted/30 p-3 text-center">
                    Complete seu cadastro para aceitar, recusar ou fazer uma contra-oferta.
                  </p>
                )
              )}
              {offer.status === "pending" && offer.parentOfferId && (
                <p className="text-sm text-muted-foreground py-2 text-center">
                  Aguardando resposta do comprador
                </p>
              )}
              {offer.status === "accepted" &&
                offer.isCurrentUserBuyer &&
                accountNav &&
                (onAddOfferToCart || onRemoveOfferFromCart) && (
                <div className="space-y-2">
                  {offerIdsInCart.includes(offer.id) ? (
                    <Button
                      onClick={() => onRemoveOfferFromCart?.(offer.id)}
                      variant="outline"
                      className="w-full py-3 rounded-lg font-medium border-destructive text-destructive hover:bg-destructive/10 flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-5 h-5" />
                      Remover do Carrinho
                    </Button>
                  ) : (
                    <Button
                      onClick={() => onAddOfferToCart?.(offer.id)}
                      className="w-full py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: "var(--orange)",
                        color: "var(--orange-foreground)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--orange-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--orange)";
                      }}
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Adicionar ao Carrinho
                    </Button>
                  )}
                </div>
              )}
              </div>
            );
            if (item.type === "single") {
              return <div key={`single-${offer.id}`}>{card}</div>;
            }
            const parentId = item.root?.id ?? item.currentOffer.parentOfferId ?? "";
            return (
              <CollapsibleNestedOffer
                key={`thread-${parentId}-${offer.id}`}
                parentOfferId={parentId}
                defaultExpanded={false}
                collapsibleLabel="Oferta original"
                collapsibleContent={
                  <ParentOfferBlock
                    parentOfferId={parentId}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    label="Oferta original"
                    totalOfferValue={item.root ? item.root.totalOfferValue : undefined}
                  />
                }
                wrapperClassName="ml-4 pl-4 border-l-2 border-primary/30"
              >
                {card}
              </CollapsibleNestedOffer>
            );
          })
        )}
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />

      {counterOfferModalOpen && selectedOffer && (
        <CounterOfferModal
          isOpen={counterOfferModalOpen && accountNav}
          onClose={() => setCounterOfferModalOpen(false)}
          originalOffer={{
            buyerName: selectedOffer.buyerName,
            cashAmount: selectedOffer.cashAmount,
            totalOfferValue: selectedOffer.totalOfferValue,
            tradeInAds: selectedOffer.tradeInAds,
          }}
          targetAd={selectedOffer.targetAd}
          onSubmit={handleSubmitCounterOffer}
        />
      )}
    </div>
  );
}